/**
 * Utility functions for LibraDashboard
 */

/**
 * Build corporate beneficiary request data from form data
 * @param {Object} formData - Form data from the create/edit modal
 * @returns {Object} - Request data for API
 */
export const buildCorporateBeneficiaryRequestData = (formData) => {
  // Build corporateBeneficiaryDetails based on transfer type
  let beneficiaryDetails = {
    transferType: formData.transferType
  };

  switch (formData.transferType) {
    case 'SEPA':
      beneficiaryDetails = {
        ...beneficiaryDetails,
        iban: formData.iban,
        bic: formData.bic,
        bankName: formData.bankName,
        accountHolderName: formData.accountHolderName,
        accountHolderAddress: formData.accountHolderAddress,
        countryCode: formData.countryCode
      };
      break;
    case 'FPS':
    case 'CHAPS':
      beneficiaryDetails = {
        ...beneficiaryDetails,
        sortCode: formData.sortCode,
        accountNumber: formData.accountNumber,
        iban: formData.iban,
        accountHolderName: formData.accountHolderName,
        bankName: formData.bankName,
        reference: formData.reference
      };
      break;
    case 'SWIFT':
      beneficiaryDetails = {
        ...beneficiaryDetails,
        swiftCode: formData.swiftCode,
        bankName: formData.bankName,
        bankAddress: formData.bankAddress,
        accountNumber: formData.accountNumber,
        accountHolderName: formData.accountHolderName,
        accountHolderAddress: formData.accountHolderAddress,
        correspondentBankSwift: formData.correspondentBankSwift,
        correspondentBankName: formData.correspondentBankName,
        lei: formData.lei,
        countryCode: formData.countryCode
      };
      break;
    case 'ACH':
      beneficiaryDetails = {
        ...beneficiaryDetails,
        routingNumber: formData.routingNumber,
        accountNumber: formData.accountNumber,
        accountHolderName: formData.accountHolderName,
        bankName: formData.bankName,
        accountType: formData.accountType,
        reference: formData.reference
      };
      break;
    case 'WIRE':
      beneficiaryDetails = {
        ...beneficiaryDetails,
        swiftCode: formData.swiftCode,
        bankName: formData.bankName,
        bankAddress: formData.bankAddress,
        accountNumber: formData.accountNumber,
        accountHolderName: formData.accountHolderName,
        accountHolderAddress: formData.accountHolderAddress,
        correspondentBankSwift: formData.correspondentBankSwift,
        correspondentBankName: formData.correspondentBankName,
        lei: formData.lei,
        countryCode: formData.countryCode,
        wireInstructions: formData.wireInstructions
      };
      break;
    case 'COLUMN_COUNTERPARTY':
      beneficiaryDetails = {
        ...beneficiaryDetails,
        counterPartyId: formData.counterPartyId
      };
      break;
    default:
      break;
  }

  return {
    corporateBeneficiaryId: formData.corporateBeneficiaryId,
    accountName: formData.accountName,
    description: formData.description,
    label: formData.label,
    currency: formData.currency,
    transferType: formData.transferType,
    corporateBeneficiaryDetails: beneficiaryDetails,
    isActive: formData.isActive || true
  };
};

/**
 * Build scheduled transfer request data from form data
 * @param {Object} formData - Form data from the create/edit modal
 * @returns {Object} - Request data for API
 */
export const buildScheduledTransferRequestData = (formData) => {
  const selectedAmountType = formData.amountType || 'FIXED';
  const selectedScheduledTransferType = formData.scheduledTransferType || 'ONE_TIME';
  const selectedStrategyType = formData.strategyType || 'PERCENTAGE_SPLIT';

  // Build amount spec based on type
  const amountSpec =
    selectedAmountType === 'AVAILABLE_BALANCE'
      ? {
          amount_type: 'AVAILABLE_BALANCE'
        }
      : {
          amount_type: 'FIXED',
          amount: Number(formData.amount)
        };

  // Parse selected beneficiary IDs (array from drag-drop)
  const beneficiaryIds = Array.isArray(formData.beneficiaryIds)
    ? formData.beneficiaryIds
    : [];

  // Build fund transfer strategy based on type
  let fundTransferStrategy;
  if (selectedStrategyType === 'PERCENTAGE_SPLIT') {
    // Create bankAccountSplits array from individual fields
    const bankAccountSplits = beneficiaryIds.map((beneficiaryId) => ({
      bank_account_id: beneficiaryId,
      percentage: parseFloat(formData[`split_${beneficiaryId}`]) || 100
    }));

    fundTransferStrategy = {
      strategy_type: 'PERCENTAGE_SPLIT',
      bank_account_splits: bankAccountSplits
    };
  } else {
    // SKIPPABLE_RATIO
    const bankAccountRatios = beneficiaryIds.map((beneficiaryId) => ({
      bank_account_id: beneficiaryId,
      ratio: parseFloat(formData[`split_${beneficiaryId}`]) || 1
    }));

    fundTransferStrategy = {
      strategy_type: 'SKIPPABLE_RATIO',
      bank_account_ratios: bankAccountRatios
    };
  }

  // Build base request
  const reqData = {
    account_id: formData.accountId,
    scheduled_transfer_type: selectedScheduledTransferType,
    fund_transfer_strategy: fundTransferStrategy,
    amount_spec: amountSpec
  };

  // Add type-specific fields
  if (selectedScheduledTransferType === 'ONE_TIME') {
    reqData.execution_time = new Date(formData.executionTime).toISOString();
  } else {
    // RECURRENT
    reqData.cron_expression = formData.cronExpression;
  }

  return reqData;
};
